import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { SimplifyModifier } from 'three/examples/jsm/modifiers/SimplifyModifier.js';
import { prefersReducedMotion } from '../utils/motion.js';

// --- look & feel knobs ---
const CYAN = 0x00aeef;
const MAGENTA = 0xec008c;
const YELLOW = 0xffe800;

const LINE_OPACITY = 0.3;
const POINT_OPACITY = 0.95;
const POINT_SIZE = 0.048;
const ROT_SPEED = 0.28; // rad/s → ~22s per full revolution
const TILT_X = -0.08; // radians, subtle top view
const CAMERA_DIST = 6; // camera distance; model is then fitted to fill the section at this distance
const FILL_V = 0.6; // max fraction of the section's height the model may occupy (responsive cap)
const FILL_H = 0.92; // max fraction of the section's width the model may occupy (responsive cap)
const CENTROID_BLEND = 0.62; // drop toward the visual-mass center: more clearance above the printer, less margin below
const WIRE_DETAIL = 0.2; // fraction of triangles kept for the wireframe (lower = fewer wires)
const MAX_PIXEL_RATIO = 2;
const DEBUG = typeof location !== 'undefined' && location.search.includes('debug3d');

function hexRgb(hex) {
  return [(hex >> 16) & 255, (hex >> 8) & 255, hex & 255];
}

/**
 * Soft radial glow sprite ("light spot") for the background atmosphere.
 * SpriteMaterial is unlit and always faces the camera, so these read as
 * floating pools of light behind/around the model.
 */
function makeGlow(colorHex, opacity) {
  const [r, g, b] = hexRgb(colorHex);
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 256;
  const ctx = canvas.getContext('2d');
  const grad = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
  grad.addColorStop(0, `rgba(${r},${g},${b},1)`);
  grad.addColorStop(0.45, `rgba(${r},${g},${b},0.55)`);
  grad.addColorStop(0.75, `rgba(${r},${g},${b},0.12)`);
  grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 256, 256);

  const texture = new THREE.CanvasTexture(canvas);
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      opacity,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
  );
  return sprite;
}

/**
 * Build a lighter wireframe: decimate the indexed geometry first so the
 * visible wire count drops, then wire each remaining triangle edge.
 */
function buildWireGeometry(geometry) {
  try {
    if (geometry.index && geometry.attributes.position) {
      const triCount = geometry.index.count / 3;
      const keep = Math.max(1, Math.round(triCount * WIRE_DETAIL));
      const toRemove = Math.floor(triCount - keep);
      if (toRemove > 0) {
        const decimated = new SimplifyModifier().modify(geometry, toRemove, true);
        return new THREE.WireframeGeometry(decimated);
      }
    }
  } catch (err) {
    // decimation failed on this geometry → fall through to full wireframe
  }
  return new THREE.WireframeGeometry(geometry);
}

/**
 * Rotating wireframe + dot version of the printed model, rendered into the
 * #printer-3d section. No scroll interaction — a slow, continuous spin.
 */
export function initPrinter3D() {
  const section = document.getElementById('printer-3d');
  if (!section) return;
  const container = section.querySelector('.printer-3d__canvas');
  const modelUrl = section.dataset.modelUrl;
  if (!container || !modelUrl) return;
  if (typeof WebGL2RenderingContext === 'undefined') return;

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
  } catch (err) {
    console.warn('[printer-3d] WebGL unavailable:', err);
    return;
  }

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 1000);
  camera.position.set(0, 0, 6);

  renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO));
  renderer.setClearColor(0x000000, 0);
  container.appendChild(renderer.domElement);

  const modelGroup = new THREE.Group();
  scene.add(modelGroup);

  /** ?debug3d: lime box = model's fitted bounds, red frame = NDC ±1 canvas edge. */
  let debugModelBox = null;
  let debugCanvasFrame = null;
  if (DEBUG) {
    const mkEdges = (color) =>
      new THREE.LineSegments(
        new THREE.BufferGeometry(),
        new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.9 })
      );
    debugModelBox = mkEdges(0x00ffaa);
    debugCanvasFrame = mkEdges(0xff0040);
    scene.add(debugModelBox, debugCanvasFrame);
  }

  // atmosphere: two "spot lights" — pink & yellow — around the model
  // (positions are sized/placed in frameModel relative to the model)
const pinkGlow = makeGlow(MAGENTA, 0.5);
  const yellowGlow = makeGlow(YELLOW, 0.42);
  scene.add(pinkGlow, yellowGlow);

  let loaded = false;
  const rawSize = new THREE.Vector3(0.001, 0.001, 0.001);
  let centroidBiasY = 0;

  const placeGlow = (sprite, fx, fy, fz, sizeFactor, G) => {
    sprite.position.set(fx * G, fy * G, -fz * G);
    sprite.scale.set(sizeFactor * G, sizeFactor * G, 1);
  };

  /** Scale the model so the whole printer always fits the section. */
  const applyFit = () => {
    camera.position.z = CAMERA_DIST;
    camera.updateProjectionMatrix();

    // NDC mapping at the model plane
    const vFovHalf = THREE.MathUtils.degToRad(camera.fov) / 2;
    const hFovHalf = Math.atan(Math.tan(vFovHalf) * camera.aspect);
    const dTanV = CAMERA_DIST * Math.tan(vFovHalf);
    const dTanH = CAMERA_DIST * Math.tan(hFovHalf);

    const hx = rawSize.x / 2;
    const hy = rawSize.y / 2;
    const hz = rawSize.z / 2;
    const tilt = TILT_X;
    const cosT = Math.cos(tilt);
    const sinT = Math.sin(tilt);

    // frameModel centers on (boxCenter + centroid bias), so AFTER that shift
    // the model's local vertical range is asymmetric:
    //   [ -(hy + biasY), +(hy - biasY) ]   → top above origin, bottom below.
    const topY = hy - centroidBiasY;
    const botY = hy + centroidBiasY;

    // Walk the 8 real corners (x, tilted-y, tilted-z). The vertical extreme is
    // yaw-invariant; the horizontal radius of a corner under yaw = sqrt(x²+z²),
    // so maxH over corners is the max footprint radius at ANY rotation angle.
    let maxH = 0;
    let maxV = 0;
    for (const sx of [-1, 1]) {
      for (const sy of [-1, 1]) {
        for (const sz of [-1, 1]) {
          const x = sx * hx;
          const y = sy === 1 ? topY : -botY;
          const z = sz * hz;
          const y1 = y * cosT - z * sinT;
          const z1 = y * sinT + z * cosT;
          maxH = Math.max(maxH, Math.hypot(x, z1));
          maxV = Math.max(maxV, Math.abs(y1));
        }
      }
    }

    const maxNdcX = maxH / dTanH;
    const maxNdcY = maxV / dTanV;
    const scale = Math.min(FILL_H / maxNdcX, FILL_V / maxNdcY);

    modelGroup.scale.set(scale, scale, scale);
    modelGroup.rotation.set(tilt, 0, 0);

    const G = Math.max(rawSize.x, rawSize.y, rawSize.z) * scale;
    placeGlow(pinkGlow, -1.15, 0.15, 1.1, 3.4, G);
    placeGlow(yellowGlow, 1.15, -0.15, 1.2, 3.0, G);

    if (DEBUG && debugModelBox && debugCanvasFrame) {
      const boxPath = (minX, maxX, minY, maxY, minZ, maxZ) => {
        const pts = [
          [minX, minY, minZ], [maxX, minY, minZ], [maxX, minY, maxZ], [minX, minY, maxZ],
          [minX, maxY, minZ], [maxX, maxY, minZ], [maxX, maxY, maxZ], [minX, maxY, maxZ],
        ];
        const pairs = [[0,3],[1,2],[0,4],[1,5],[2,6],[3,7],[4,7],[5,6],[0,1],[3,2],[4,5],[7,6]];
        const v = [];
        for (const [a,b] of pairs) { v.push(...pts[a], ...pts[b]); }
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.Float32BufferAttribute(v, 3));
        return g;
      };
      // Model's true rendered bounds (post-shift; yaw keeps these extremes exact).
      debugModelBox.geometry.dispose();
      debugModelBox.geometry = boxPath(
        -hx * scale, hx * scale,
        -botY * scale, topY * scale,
        -hz * scale, hz * scale
      );
      debugModelBox.position.set(0, -centroidBiasY * scale, 0);
      // Canvas extent in world space at NDC ±1 (the clipping square).
      debugCanvasFrame.geometry.dispose();
      debugCanvasFrame.geometry = boxPath(
        -dTanH, dTanH, -dTanV, dTanV, -0.001, 0.001
      );
      debugCanvasFrame.position.set(0, 0, 0);
    }

    const rect = container.getBoundingClientRect();
    const hPx = Math.max(container.clientHeight, 1);
    const ndcTop = (topY * scale) / dTanV;
    const ndcBot = (-botY * scale) / dTanV;
    console.info('[printer-3d] FITTED', {
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      rect: [rect.width.toFixed(0), rect.height.toFixed(0), rect.top.toFixed(0), rect.bottom.toFixed(0)],
      pixels: [Math.round((hPx / 2) * (1 - ndcTop)), Math.round((hPx / 2) * (1 - ndcBot)), hPx],
      rawSize: rawSize.toArray().map((v) => +v.toFixed(3)),
      aspect: +camera.aspect.toFixed(2),
      biasY: +centroidBiasY.toFixed(3),
      scale: +scale.toFixed(2),
      maxNdc: [+maxNdcX.toFixed(3), +maxNdcY.toFixed(3)],
    });
    return scale;
  };

const onResize = () => {
    const w = Math.max(container.clientWidth, 1);
    const h = Math.max(container.clientHeight, 1);
    renderer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    if (loaded) applyFit();
  };
  onResize();
  window.addEventListener('resize', onResize);

  /** Turn every mesh into a wireframe + vertex-dots pair, keeping its transform. */
  const buildView = (model) => {
    model.updateMatrixWorld(true);
    let count = 0;
    let vSumX = 0;
    let vSumY = 0;
    let vSumZ = 0;
    let vN = 0;
    const tmpV = new THREE.Vector3();
    model.traverse((child) => {
      if (!child.isMesh || !child.geometry) return;

      const positions = child.geometry.attributes.position;
      if (positions) {
        for (let i = 0; i < positions.count; i++) {
          tmpV.fromBufferAttribute(positions, i).applyMatrix4(child.matrixWorld);
          vSumX += tmpV.x;
          vSumY += tmpV.y;
          vSumZ += tmpV.z;
          vN++;
        }
      }

      const wire = new THREE.LineSegments(
        buildWireGeometry(child.geometry),
        new THREE.LineBasicMaterial({
          color: CYAN,
          transparent: true,
          opacity: LINE_OPACITY,
        })
      );

      const dots = new THREE.Points(
        child.geometry,
        new THREE.PointsMaterial({
          color: CYAN,
          size: POINT_SIZE,
          transparent: true,
          opacity: POINT_OPACITY,
          depthWrite: false,
        })
      );

      const holder = new THREE.Object3D();
      holder.applyMatrix4(child.matrixWorld);
      holder.add(wire, dots);
      modelGroup.add(holder);
      count++;
    });
    return { count, centroid: vN > 0 ? [vSumX / vN, vSumY / vN, vSumZ / vN] : null };
  };

  /**
   * Center the model, then delegate to applyFit() which scales the whole
   * printer to fill the section without any clipping.
   */
  const frameModel = (centroid = null) => {
    const box = new THREE.Box3().setFromObject(modelGroup);
    if (box.isEmpty()) return;
    const center = new THREE.Vector3();
    box.getCenter(center);
    box.getSize(rawSize);

    // The printer's visual mass sits above its box center; drop the model
    // toward the vertex centroid so it reads as nicely placed, not top-heavy.
    // applyFit() reads centroidBiasY so its corner math matches the render.
    centroidBiasY = centroid ? (centroid[1] - center.y) * CENTROID_BLEND : 0;

    modelGroup.position.set(-center.x, -(center.y + centroidBiasY), -center.z);

    applyFit();
    loaded = true;
  };

  let rafId = 0;
  let last = 0;
  let inView = false;
  const reduceMotion = prefersReducedMotion();

  const render = () => renderer.render(scene, camera);

  const loop = (now) => {
    rafId = requestAnimationFrame(loop);
    const delta = Math.min((now - last) / 1000, 1);
    last = now;
    modelGroup.rotation.y += ROT_SPEED * delta;
    render();
  };

  const start = () => {
    if (rafId || !loaded) return;
    if (reduceMotion) {
      render();
      return;
    }
    last = performance.now();
    rafId = requestAnimationFrame(loop);
  };

  const stop = () => {
    cancelAnimationFrame(rafId);
    rafId = 0;
  };

  // Battery-friendly: only render while the section is near the viewport.
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        if (inView) start();
        else stop();
      },
      { rootMargin: '120px 0px' }
    ).observe(container);
  } else {
    inView = true;
  }

  const loader = new GLTFLoader();
  loader.load(
    modelUrl,
    (gltf) => {
      const view = buildView(gltf.scene);
      if (!view.count) return;
      frameModel(view.centroid);
      if (inView) start();
    },
    undefined,
    (err) => console.warn('[printer-3d] Failed to load model:', err)
  );
}